import { DOG_KENNEL_LAYOUT_ID } from '../ssb_constants';
import type {
	TDisableReason,
	TSizeRestrictionInput,
	TSizeRestrictionResult,
	TSizeRestrictionSize,
} from './type';

const HORSERUNIN_SHED_TYPES = ['structure_lofted_horserunin', 'structure_aframe_horserunin'];

/**
 * Checks if a W x L has at least one height allowed in the zone.
 * @param {TSizeRestrictionSize} size - The W x L data (decoded from data-sizes).
 * @param {string} zoneId - The restriction zone id.
 * @returns {boolean}
 */
const hasSizeInZone = (size: TSizeRestrictionSize, zoneId: string): boolean => {
	return (size.data || []).some((item) => {
		if (!item.conditionZone) return false;
		// conditionZone can be an array or an object (php assoc array)
		return Object.values(item.conditionZone).map(String).includes(zoneId);
	});
};

/**
 * Get disabled sizes (W x L and height) by zone, layout, shed type and selected W x L.
 * Also returns the new selection when the selected size is not valid anymore.
 * @param {TSizeRestrictionInput} input - Data read from the size selects and the builder.
 * @returns {TSizeRestrictionResult}
 */
export const computeSizeRestrictions = (input: TSizeRestrictionInput): TSizeRestrictionResult => {
	const { zone, layout, shedType, openingTotalHeight, sizes, heightOptions, selected } = input;

	const selection = { ...selected };
	const selectionChanged = { area: false, height: false };
	const heightChangeReasons: TDisableReason[] = [];

	// custom size mode: bypass all restrictions
	if (input.isCustomSize) {
		return {
			area: sizes.map((s) => ({ width: s.width, depth: s.depth, reasons: [] })),
			height: heightOptions.map((h) => ({ height: h.height, reasons: [], hidden: false })),
			selection,
			selectionChanged,
			heightChangeReasons,
			allowAddToCart: null,
			hideHeightSection: null,
			notice: null,
		};
	}

	const isZoneActive = zone.enabled && zone.id !== null && zone.id !== '';
	const zoneId = String(zone.id);
	const isHorserunin = HORSERUNIN_SHED_TYPES.includes(shedType);
	const isScout = !!layout && layout.id === DOG_KENNEL_LAYOUT_ID.SCOUT;

	// W x L options
	const area = sizes.map((s) => {
		const reasons: TDisableReason[] = [];
		if (isZoneActive && s.data && !hasSizeInZone(s, zoneId)) reasons.push('zone');
		if (layout && (s.width < layout.limitWidth || s.depth < layout.limitDepth))
			reasons.push('layout');
		if (isHorserunin && s.depth < 10) reasons.push('horserunin');
		if (isScout && s.depth > 8) reasons.push('scout');
		return { width: s.width, depth: s.depth, reasons };
	});

	// Heights of the selected W x L, null if the option has no data-sizes
	const selectedSize = sizes.find(
		(s) => s.width === selected.width && s.depth === selected.depth,
	);
	const wlHeights = selectedSize?.data
		? selectedSize.data.map((item) => Number(item.height))
		: null;

	// Height options
	const height = heightOptions.map((h) => {
		const reasons: TDisableReason[] = [];
		if (isZoneActive && !h.zones.map(String).includes(zoneId)) reasons.push('zone');
		if (layout && h.height < layout.limitHeight) reasons.push('layout');
		if (isHorserunin && h.height <= openingTotalHeight) reasons.push('horserunin');
		if (wlHeights && !wlHeights.includes(h.height)) reasons.push('wl');
		const hidden = input.heightInvalidMode === 'hide' && reasons.includes('wl');
		return { height: h.height, reasons, hidden };
	});

	const validHeights = height.filter((h) => h.reasons.length === 0);
	const validAreas = area.filter((a) => a.reasons.length === 0);

	// Change height only when disabled by zone or W x L, layout is handled by restoreSize()
	const currentHeight = height.find((h) => h.height === selected.height);
	const heightTriggers = currentHeight
		? currentHeight.reasons.filter((r) => r === 'zone' || r === 'wl')
		: [];
	const isHeightInvalid = heightTriggers.length > 0 || (!currentHeight && wlHeights !== null);

	if (isHeightInvalid && validHeights.length && validHeights[0].height !== selected.height) {
		selection.height = validHeights[0].height;
		selectionChanged.height = true;
		heightChangeReasons.push(
			...(heightTriggers.length ? heightTriggers : (['wl'] as TDisableReason[])),
		);
	}

	// Change W x L only in zone mode and when there is still a valid height
	const currentArea = area.find((a) => a.width === selected.width && a.depth === selected.depth);
	if (isZoneActive && validHeights.length && currentArea?.reasons.length && validAreas.length) {
		selection.width = validAreas[0].width;
		selection.depth = validAreas[0].depth;
		selectionChanged.area = true;
	}

	let notice: TSizeRestrictionResult['notice'] = null;
	if (isZoneActive && !validHeights.length) {
		notice = { kind: 'dialog', type: 'noSizeForZone' };
	} else if (
		selectionChanged.height &&
		selected.height &&
		heightTriggers.includes('wl') &&
		!heightTriggers.includes('zone')
	) {
		notice = {
			kind: 'alert',
			type: 'heightResized',
			from: selected.height,
			to: selection.height,
		};
	}

	return {
		area,
		height,
		selection,
		selectionChanged,
		heightChangeReasons,
		allowAddToCart: isZoneActive ? validHeights.length > 0 : null,
		hideHeightSection: wlHeights !== null ? validHeights.length <= 1 : null,
		notice,
	};
};
